import type { ReactNode } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { messageKeyForError } from "../i18n/errors";
import { Button } from "../components/ui/Button";
import { ErrorState } from "../components/ui/ErrorState";

export const TH_CLASS =
  "sticky top-0 z-10 border-b-2 border-ink bg-board px-3 py-2.5 text-left font-display text-note font-bold tracking-[0.01em] text-ink-2 uppercase whitespace-nowrap";
export const TD_CLASS = "border-b border-hairline px-3 py-3 align-middle text-operate text-ink";

export interface DenseTableProps {
  label: string;
  children: ReactNode;
  /** Caps the height so the sticky header row stays in view while the body scrolls. */
  scroll?: boolean;
}

/** Dense ruled table on a board. Rows are label-grid rules, not cards. */
export function DenseTable({ label, children, scroll = false }: DenseTableProps) {
  const overflow = scroll ? "max-h-[70vh] overflow-auto" : "overflow-x-auto md:overflow-visible";
  return (
    <div className={`rounded-panel bg-board shadow-board ${overflow}`}>
      <table aria-label={label} className="w-full border-collapse text-left">
        {children}
      </table>
    </div>
  );
}

export type MarkTone = "blue" | "red" | "hollow" | "muted";

const MARK_CLASS: Record<MarkTone, string> = {
  blue: "bg-blue",
  red: "bg-red",
  hollow: "border-2 border-edge",
  muted: "bg-ink-3",
};

/** Status with a faceoff-style mark: blue active, red suspended, hollow pending, ink-3 deleted. */
export function StatusLabel({ tone, children }: { tone: MarkTone; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 text-note font-semibold whitespace-nowrap text-ink-2">
      <span
        aria-hidden="true"
        className={`inline-block size-2 shrink-0 rounded-full ${MARK_CLASS[tone]}`}
      />
      {children}
    </span>
  );
}

export interface QuotaBarProps {
  fraction: number;
  label: string;
  className?: string;
}

/** Small rink-length bar. Blue below 90 percent, red at or above it. */
export function QuotaBar({ fraction, label, className = "w-32" }: QuotaBarProps) {
  const percent = Math.round(Math.min(1, Math.max(0, fraction)) * 100);
  return (
    <div className={`space-y-1 ${className}`}>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="h-1.5 overflow-hidden rounded-full bg-ice-2"
      >
        <div
          className={`h-full rounded-full transition-[width] duration-[420ms] ease-out ${percent >= 90 ? "bg-red" : "bg-blue"}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="text-micro tabular-nums text-ink-3">{label}</p>
    </div>
  );
}

export interface BoardProps {
  title: string;
  children: ReactNode;
}

/** A board: white panel on the ice with a condensed heading. Operate sections are boards. */
export function Board({ title, children }: BoardProps) {
  return (
    <section className="space-y-3 rounded-panel bg-board p-4 shadow-board md:p-5">
      <h2 className="font-display text-2xl leading-none font-extrabold tracking-[0.01em] uppercase">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Query failure with the one recovery the admin can take: try again. */
export function QueryError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useI18n();
  return (
    <ErrorState action={<Button onClick={onRetry}>{t("app.retry")}</Button>}>
      {t(messageKeyForError(error))}
    </ErrorState>
  );
}
